import * as yup from 'yup';

// Common validation patterns
const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const phoneRegex = /^[+]?[\d\s\-().]{7,20}$/;

const hasValidPhoneDigitCount = (value: string) => {
  const digits = value.replace(/\D/g, '');
  return digits.length >= 7 && digits.length <= 15;
};
const employeeIdRegex = /^[A-Za-z0-9\-_]+$/;

// User validation schema
export const userValidationSchema = yup.object({
  email: yup
    .string()
    .required('Email address is required')
    .matches(emailRegex, 'Please enter a valid email address')
    .max(254, 'Email address is too long'),
  
  first_name: yup
    .string()
    .required('First name is required')
    .min(1, 'First name cannot be empty')
    .max(150, 'First name cannot exceed 150 characters')
    .trim(),
  
  last_name: yup
    .string()
    .required('Last name is required')
    .min(1, 'Last name cannot be empty')
    .max(150, 'Last name cannot exceed 150 characters')
    .trim(),
  
  role: yup
    .string()
    .required('Role is required')
    .oneOf(['admin', 'manager', 'employee'], 'Please select a valid role'),
  
  employment_status: yup
    .string()
    .required('Employment status is required')
    .oneOf(['active', 'inactive', 'on_leave', 'terminated'], 'Please select a valid employment status'),
  
  phone_number: yup
    .string()
    .optional()
    .nullable()
    .test('phone-format', 'Please enter a valid phone number', value => {
      if (!value) return true; // Allow empty values
      return phoneRegex.test(value) && hasValidPhoneDigitCount(value);
    })
    .max(20, 'Phone number is too long'),
  
  employee_id: yup
    .string()
    .optional()
    .nullable()
    .test('employee-id-format', 'Employee ID can only contain letters, numbers, hyphens, and underscores', value => {
      if (!value) return true; // Allow empty values
      return employeeIdRegex.test(value);
    })
    .max(50, 'Employee ID is too long'),
  
  timezone: yup
    .string()
    .required('Timezone is required')
    .max(50, 'Timezone is too long'),
  
  hire_date: yup
    .string()
    .optional()
    .test('future-date', 'Hire date cannot be in the future', value => {
      if (!value) return true;
      return new Date(value) <= new Date();
    }),
  
  department_id: yup
    .number()
    .optional()
    .nullable(),
  
  location_id: yup
    .number()
    .optional()
    .nullable(),
});

// Department validation schema
export const departmentValidationSchema = yup.object({
  name: yup
    .string()
    .required('Department name is required')
    .min(1, 'Department name cannot be empty')
    .max(100, 'Department name cannot exceed 100 characters')
    .trim(),
  
  description: yup
    .string()
    .optional()
    .max(500, 'Description cannot exceed 500 characters'),
  
  is_active: yup
    .boolean()
    .default(true),
});

// Location validation schema
export const locationValidationSchema = yup.object({
  name: yup
    .string()
    .required('Location name is required')
    .min(1, 'Location name cannot be empty')
    .max(100, 'Location name cannot exceed 100 characters')
    .trim(),
  
  address: yup
    .string()
    .optional()
    .max(500, 'Address cannot exceed 500 characters'),
  
  is_active: yup
    .boolean()
    .default(true),
});

// Assignment validation schema
export const assignmentValidationSchema = yup.object({
  content_type: yup
    .string()
    .required('Content type is required'),
  
  object_id: yup
    .number()
    .required('Content is required')
    .positive('Please select valid content'),
  
  assignee_ids: yup
    .array()
    .of(yup.number().positive())
    .min(1, 'At least one assignee is required'),
  
  due_date: yup
    .date()
    .optional()
    .min(new Date(), 'Due date cannot be in the past'),
  
  assignment_type: yup
    .string()
    .required('Assignment type is required')
    .oneOf(['individual', 'department', 'location'], 'Please select a valid assignment type'),
});

// Training validation schema
export const trainingValidationSchema = yup.object({
  title: yup
    .string()
    .required('Training title is required')
    .min(1, 'Title cannot be empty')
    .max(200, 'Title cannot exceed 200 characters')
    .trim(),
  
  description: yup
    .string()
    .optional()
    .max(1000, 'Description cannot exceed 1000 characters'),
  
  category: yup
    .string()
    .optional()
    .max(100, 'Category cannot exceed 100 characters'),
  
  estimated_duration: yup
    .number()
    .optional()
    .min(1, 'Duration must be at least 1 minute')
    .max(1440, 'Duration cannot exceed 24 hours'),
});

// Section validation schema
export const sectionValidationSchema = yup.object({
  title: yup
    .string()
    .required('Section title is required')
    .min(1, 'Title cannot be empty')
    .max(200, 'Title cannot exceed 200 characters')
    .trim(),
  
  description: yup
    .string()
    .optional()
    .max(500, 'Description cannot exceed 500 characters'),
  
  estimated_minutes: yup
    .number()
    .optional()
    .min(1, 'Duration must be at least 1 minute')
    .max(480, 'Duration cannot exceed 8 hours'),
});

// Content block validation schema
export const contentBlockValidationSchema = yup.object({
  title: yup
    .string()
    .required('Block title is required')
    .min(1, 'Title cannot be empty')
    .max(200, 'Title cannot exceed 200 characters')
    .trim(),
  
  block_type: yup
    .string()
    .required('Block type is required')
    .oneOf(['text', 'video', 'link', 'image', 'quiz', 'test', 'interactive'], 'Please select a valid block type'),
  
  content: yup
    .string()
    .optional()
    .max(10000, 'Content is too long'),
  
  order: yup
    .number()
    .required('Order is required')
    .min(1, 'Order must be at least 1'),
});

// Quiz question validation schema
export const quizQuestionValidationSchema = yup.object({
  question_text: yup
    .string()
    .required('Question text is required')
    .min(1, 'Question cannot be empty')
    .max(1000, 'Question is too long')
    .trim(),
  
  question_type: yup
    .string()
    .required('Question type is required')
    .oneOf(['multiple_choice', 'true_false', 'text'], 'Please select a valid question type'),
  
  correct_answer: yup
    .string()
    .required('Correct answer is required'),
  
  options: yup
    .array()
    .of(yup.string().required('Option cannot be empty'))
    .when('question_type', {
      is: 'multiple_choice',
      then: (schema) => schema.min(2, 'At least 2 options are required for multiple choice'),
      otherwise: (schema) => schema.optional(),
    }),
  
  explanation: yup
    .string()
    .optional()
    .max(1000, 'Explanation is too long'),
});

// Password validation schema
export const passwordValidationSchema = yup.object({
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Password must contain at least one uppercase letter, one lowercase letter, and one number'
    ),
  
  password_confirm: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('password')], 'Passwords do not match'),
});

// Login validation schema
export const loginValidationSchema = yup.object({
  email: yup
    .string()
    .required('Email address is required')
    .matches(emailRegex, 'Please enter a valid email address'),
  
  password: yup
    .string()
    .required('Password is required'),
});

// Invitation acceptance validation schema
export const invitationAcceptanceSchema = yup.object({
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Password must contain at least one uppercase letter, one lowercase letter, and one number'
    ),
  
  password_confirm: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('password')], 'Passwords do not match'),
});

// Company with admin validation schema
export const companyWithAdminValidationSchema = yup.object({
  // Company fields
  name: yup
    .string()
    .required('Company name is required')
    .min(1, 'Company name cannot be empty')
    .max(200, 'Company name cannot exceed 200 characters')
    .trim(),
  
  timezone: yup
    .string()
    .required('Timezone is required')
    .max(50, 'Timezone is too long'),
  
  legal_name: yup
    .string()
    .nullable()
    .optional()
    .max(200, 'Legal name cannot exceed 200 characters'),
  
  industry: yup
    .string()
    .nullable()
    .optional()
    .max(50, 'Industry cannot exceed 50 characters'),
  
  company_size: yup
    .string()
    .nullable()
    .optional()
    .max(20, 'Company size cannot exceed 20 characters'),
  
  description: yup
    .string()
    .nullable()
    .optional()
    .max(2000, 'Description cannot exceed 2000 characters'),
  
  // Admin user fields
  email: yup
    .string()
    .required('Email address is required')
    .matches(emailRegex, 'Please enter a valid email address')
    .max(254, 'Email address is too long'),
  
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters'),
  
  password_confirm: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('password')], 'Passwords do not match'),
  
  first_name: yup
    .string()
    .required('First name is required')
    .min(1, 'First name cannot be empty')
    .max(150, 'First name cannot exceed 150 characters')
    .trim(),
  
  last_name: yup
    .string()
    .required('Last name is required')
    .min(1, 'Last name cannot be empty')
    .max(150, 'Last name cannot exceed 150 characters')
    .trim(),
  
  is_superuser: yup
    .boolean()
    .default(false),
});

// Export all schemas for easy access
export const validationSchemas = {
  user: userValidationSchema,
  department: departmentValidationSchema,
  location: locationValidationSchema,
  assignment: assignmentValidationSchema,
  training: trainingValidationSchema,
  section: sectionValidationSchema,
  contentBlock: contentBlockValidationSchema,
  quizQuestion: quizQuestionValidationSchema,
  password: passwordValidationSchema,
  login: loginValidationSchema,
  invitationAcceptance: invitationAcceptanceSchema,
  companyWithAdmin: companyWithAdminValidationSchema,
};
